export const fmtInt = (n) => new Intl.NumberFormat('en-AU').format(Math.round(n ?? 0))
export const fmtPct = (n, d = 1) => `${((n ?? 0) * 100).toFixed(d)}%`
export const fmtMoney = (n, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency, maximumFractionDigits: (n ?? 0) >= 1000 ? 0 : 2 }).format(n ?? 0)
  } catch {
    return `$${(n ?? 0).toFixed(2)}`
  }
}
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '-')
export const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-')
export const fmtSeconds = (s) => (s == null ? '-' : s < 60 ? `${s.toFixed(0)}s` : `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`)

/* ------------------------------------------------ dates in the store's zone */

/**
 * A popup's run window is stored as absolute instants, but the merchant chose
 * them as days in their store's timezone. Showing them back in the browser's
 * zone would tell someone in London that their Sydney sale ends a day early, so
 * everything to do with a schedule goes through these three.
 */

/** An ISO instant as a date in `tz`, e.g. "31 Dec 2026". */
export const fmtDateIn = (iso, tz) => {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleDateString('en-AU', { timeZone: tz || undefined, day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return fmtDate(iso)
  }
}

/**
 * Minutes `tz` is ahead of UTC at instant `t`. Read the wall clock there, treat
 * it as if it were UTC, and the difference from the real instant is the offset.
 */
function tzOffsetMinutes(tz, t) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(t))
  const p = {}
  for (const x of parts) p[x.type] = x.value
  const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second)
  return (asUtc - Math.floor(t / 1000) * 1000) / 60000
}

/**
 * "2026-12-31" in `tz` to the ISO instant at the start or the END of that day.
 *
 * `edge: 'end'` is what makes "stop on 31 December" mean 23:59:59.999 in the
 * store's zone rather than midnight UTC, which in Sydney would have cut the last
 * day short by eleven hours.
 *
 * The offset is read twice: once at the naive instant, then again at the result.
 * A single pass lands an hour out on the two days a year the zone changes.
 */
export function dayToIso(day, tz, edge = 'start') {
  if (!day) return ''
  const [y, m, d] = String(day).split('-').map(Number)
  if (!y || !m || !d) return ''
  const wall = edge === 'end' ? Date.UTC(y, m - 1, d, 23, 59, 59, 999) : Date.UTC(y, m - 1, d, 0, 0, 0, 0)
  try {
    const first = wall - tzOffsetMinutes(tz, wall) * 60000
    return new Date(wall - tzOffsetMinutes(tz, first) * 60000).toISOString()
  } catch {
    return new Date(wall).toISOString() // unknown zone: fall back to UTC rather than losing the date
  }
}

/** The reverse: an ISO instant back to the YYYY-MM-DD an <input type="date"> wants, read in `tz`. */
export function isoToDay(iso, tz) {
  const t = Date.parse(iso ?? '')
  if (!Number.isFinite(t)) return ''
  try {
    // en-CA formats as YYYY-MM-DD, which is exactly the input's value format.
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(t))
  } catch {
    return new Date(t).toISOString().slice(0, 10)
  }
}

/** Change vs previous period: {text, dir: 'up'|'down'|'flat'} */
export function delta(cur, prev) {
  if (!prev) return cur ? { text: 'new', dir: 'up' } : { text: '-', dir: 'flat' }
  const d = cur / prev - 1
  if (Math.abs(d) < 0.005) return { text: '0%', dir: 'flat' }
  return { text: `${d > 0 ? '+' : ''}${(d * 100).toFixed(0)}%`, dir: d > 0 ? 'up' : 'down' }
}

export const STATUS_BADGE = { live: 'badge-approved', paused: 'badge-pending', draft: 'badge-expired', archived: 'badge-denied' }
export const GOAL_LABEL = { email: 'Email', email_sms: 'Email + SMS', sms: 'SMS', announcement: 'Announcement', survey: 'Survey', quiz: 'Quiz', lead: 'Lead', custom: 'Custom' }
